/**
 * Helpers for computing date boundaries in a specific IANA timezone.
 *
 * Browsers (and their local clock) use whatever timezone the device is set to,
 * which is not necessarily the store's configured timezone. Using
 * `Date#setHours(0,0,0,0)` produces a boundary in the browser's local zone,
 * not the store-local day boundary (e.g. Asia/Phnom_Penh, UTC+07:00).
 *
 * Mirrors functions/src/timezone.ts so client and server compute the same
 * store-local day boundaries.
 */

export const DEFAULT_STORE_TIMEZONE = 'Asia/Phnom_Penh';

function getZonedParts(date: Date, timeZone: string): { y: number; m: number; d: number } {
   const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
   });
   const parts = dtf.formatToParts(date);
   const year = Number(parts.find((p) => p.type === 'year')?.value);
   const month = Number(parts.find((p) => p.type === 'month')?.value);
   const day = Number(parts.find((p) => p.type === 'day')?.value);
   return { y: year, m: month, d: day };
}

/**
 * Hour (0–23) of `date` as observed in `timeZone`.
 *
 * Always prefer this over `Date#getHours()` when the answer feeds a
 * store-side decision — `getHours()` reads the *device* clock, which is
 * frequently not the store's timezone.
 */
export function getZonedHour(date: Date, timeZone: string): number {
   const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      hourCycle: 'h23',
   });
   const hourPart = dtf.formatToParts(date).find((p) => p.type === 'hour')?.value;
   const hour = Number(hourPart);
   if (!Number.isFinite(hour)) return date.getHours(); // defensive fallback
   // Some implementations emit "24" for midnight even under h23.
   return hour === 24 ? 0 : hour;
}

/** Formats y/m/d as a sortable 'YYYY-MM-DD' key. */
function formatDateKey(y: number, m: number, d: number): string {
   return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The calendar day of `date` as observed in `timeZone`, as 'YYYY-MM-DD'. */
export function zonedDateKey(date: Date, timeZone: string): string {
   const { y, m, d } = getZonedParts(date, timeZone);
   return formatDateKey(y, m, d);
}

/** UTC offset of `timeZone` at `utcDate`, in minutes (Phnom Penh = 420). */
export function getTimeZoneOffsetMinutes(timeZone: string, utcDate: Date): number {
   const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
   });

   const tzName = dtf.formatToParts(utcDate).find((p) => p.type === 'timeZoneName')?.value;

   if (!tzName) return 0;
   if (tzName === 'GMT' || tzName === 'UTC') return 0;

   const match = /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(tzName);
   if (!match) return 0;

   const sign = match[1] === '-' ? -1 : 1;
   const hours = Number(match[2]);
   const minutes = match[3] ? Number(match[3]) : 0;
   return sign * (hours * 60 + minutes);
}

/**
 * The calendar day `date` falls on in `timeZone`, as a device-local midnight.
 *
 * For dates that were saved as a store-local day or month (e.g. a payroll
 * run's month, saved as midnight on the 1st in Phnom Penh). Reading those
 * with `getMonth()` or the date pipe uses the device's time zone, so a
 * device set to UTC shows 1 Oct 00:00 (+07) as 30 Sep — "September". The
 * returned Date carries the store's calendar day, so `getMonth()`,
 * `getDate()` and the date pipe give the store's answer on any device.
 */
export function zonedCalendarDate(date: Date, timeZone: string): Date {
   const { y, m, d } = getZonedParts(date, timeZone);
   return new Date(y, m - 1, d);
}

/** Returns the instant (in UTC) corresponding to local midnight in `timeZone` on the given calendar date. */
function startOfZonedCalendarDay(y: number, m: number, d: number, timeZone: string): Date {
   // Not the correct instant yet; used as an anchor to get the timezone offset for that local date.
   const utcMidnightAnchor = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
   const offsetMinutes = getTimeZoneOffsetMinutes(timeZone, utcMidnightAnchor);

   return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0) - offsetMinutes * 60 * 1000);
}

/** Returns the instant (in UTC) corresponding to local midnight in `timeZone` on `date`'s local calendar day. */
export function startOfDayInTimeZone(date: Date, timeZone: string): Date {
   const { y, m, d } = getZonedParts(date, timeZone);
   return startOfZonedCalendarDay(y, m, d, timeZone);
}

// ─── Calendar dates ↔ store instants ────────────────────────────────
//
// A "calendar date" is a device-local Date whose getFullYear / getMonth /
// getDate (and clock fields) name a store day — what a datepicker returns,
// what `new Date(y, m, d)` builds and what zonedCalendarDate gives back. The
// helpers below turn one into the instant the STORE's clock shows that wall
// time, for Firestore queries and writes. On a device already on the store's
// zone they return the same instant as the input, so ids and stored values
// don't change there.

/**
 * The instant at which the clock in `timeZone` shows the wall time that
 * `local` shows on the device (its year, month, day, hours, … fields).
 *
 *   device UTC, local = 1 Oct 00:00 → 30 Sep 17:00Z (1 Oct 00:00 in Phnom Penh)
 */
export function wallClockInTimeZone(local: Date, timeZone: string): Date {
   const wall = Date.UTC(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      local.getHours(),
      local.getMinutes(),
      local.getSeconds(),
      local.getMilliseconds(),
   );
   // The offset at the wall time read as UTC is right except within an
   // offset change; one correction pass settles it.
   const first = getTimeZoneOffsetMinutes(timeZone, new Date(wall));
   const guess = wall - first * 60_000;
   const second = getTimeZoneOffsetMinutes(timeZone, new Date(guess));
   return second === first ? new Date(guess) : new Date(wall - second * 60_000);
}

/** Store-local [00:00, 23:59:59.999] of the calendar date `day`, as instants. */
export function zonedDayRange(day: Date, timeZone: string): { start: Date; end: Date } {
   const y = day.getFullYear();
   const m = day.getMonth();
   const d = day.getDate();
   return {
      start: wallClockInTimeZone(new Date(y, m, d), timeZone),
      end: wallClockInTimeZone(new Date(y, m, d, 23, 59, 59, 999), timeZone),
   };
}

/** Store-local first 00:00 to last 23:59:59.999 of the calendar month of `month`. */
export function zonedMonthRange(month: Date, timeZone: string): { start: Date; end: Date } {
   const y = month.getFullYear();
   const m = month.getMonth();
   return {
      start: wallClockInTimeZone(new Date(y, m, 1), timeZone),
      end: wallClockInTimeZone(new Date(y, m + 1, 0, 23, 59, 59, 999), timeZone),
   };
}

/** Today in the store, as a calendar date (device-local midnight of the store's day). */
export function zonedToday(timeZone: string, now: Date = new Date()): Date {
   return zonedCalendarDate(now, timeZone);
}

/**
 * `instant` as the store's wall clock on a device-local Date: date AND time
 * fields read as the store sees them. For editing a stored time with
 * `setHours` (then wallClockInTimeZone back) or showing it with the date pipe.
 */
export function zonedWallClock(instant: Date, timeZone: string): Date {
   const offsetMs = getTimeZoneOffsetMinutes(timeZone, instant) * 60_000;
   const shifted = new Date(instant.getTime() + offsetMs);
   return new Date(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate(),
      shifted.getUTCHours(),
      shifted.getUTCMinutes(),
      shifted.getUTCSeconds(),
      shifted.getUTCMilliseconds(),
   );
}

// ─── Operational ("session") dates ──────────────────────────────────
//
// A trading session runs from `openHour` on day N until `closeHour` on
// day N+1. An instant that falls in the overnight tail (zoned hour <
// closeHour) still belongs to the PREVIOUS calendar day's session.
//
//   closeHour = 3
//   Mon 22:00 → 'Mon'   (evening of Monday's session)
//   Tue 01:00 → 'Mon'   (overnight tail of Monday's session)
//   Tue 15:00 → 'Tue'   (new session)

/**
 * The operational date of `date` — i.e. the calendar day its session
 * STARTED on, in `timeZone` — as a sortable 'YYYY-MM-DD' key.
 *
 * Returned as a string rather than a Date so callers can compare sessions
 * with a single `===` instead of a three-way year/month/day comparison,
 * and so the value can be stored and queried directly in Firestore.
 */
export function operationalDateKey(date: Date, closeHour: number, timeZone: string): string {
   const { y, m, d } = getZonedParts(date, timeZone);
   const hour = getZonedHour(date, timeZone);

   if (hour >= closeHour) return formatDateKey(y, m, d);

   // Roll back one calendar day. Done via Date.UTC so month/year boundaries
   // and leap days are handled for us — this is pure calendar arithmetic on
   // the zoned date label, so no timezone conversion is involved.
   const previous = new Date(Date.UTC(y, m - 1, d - 1, 0, 0, 0, 0));
   return formatDateKey(
      previous.getUTCFullYear(),
      previous.getUTCMonth() + 1,
      previous.getUTCDate(),
   );
}

/**
 * The instant (in UTC) at which the operational day containing `date`
 * begins — that is, local midnight in `timeZone` on the session's start day.
 *
 * Use this, not `startOfDayInTimeZone`, when querying "everything in the
 * session that is currently running": at 01:00 with closeHour=3, calendar
 * midnight has already passed but the session started the previous evening.
 */
export function startOfOperationalDay(date: Date, closeHour: number, timeZone: string): Date {
   const [y, m, d] = operationalDateKey(date, closeHour, timeZone).split('-').map(Number);
   return startOfZonedCalendarDay(y, m, d, timeZone);
}

/**
 * `timeZone`'s offset as the DatePipe's `timezone` argument ('+0700'), so a
 * stored time shows on the store's clock: `{{ t | date:'h:mm a':offset }}`.
 * DatePipe takes offsets, not IANA names; the offset is taken at `at`.
 */
export function datePipeOffset(timeZone: string, at: Date = new Date()): string {
   const minutes = getTimeZoneOffsetMinutes(timeZone, at);
   const sign = minutes < 0 ? '-' : '+';
   const abs = Math.abs(minutes);
   return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}${String(abs % 60).padStart(2, '0')}`;
}

/** "UTC+07:00" style label for an offset in minutes. */
export function formatUtcOffset(minutes: number): string {
   const sign = minutes < 0 ? '-' : '+';
   const abs = Math.abs(minutes);
   const hh = String(Math.floor(abs / 60)).padStart(2, '0');
   const mm = String(abs % 60).padStart(2, '0');
   return `UTC${sign}${hh}:${mm}`;
}

/**
 * Whether the device clock runs in a different UTC offset than the store at
 * `now`. The app builds "today" and "this month" from the device clock, so a
 * mismatch shifts payroll, attendance and report dates by a day.
 *
 * `deviceOffsetMinutes` is `-now.getTimezoneOffset()` (east of UTC positive);
 * it is a parameter so tests don't depend on the machine's zone.
 */
export function deviceTimeZoneMismatch(
   now: Date,
   storeTimeZone: string,
   deviceOffsetMinutes = -now.getTimezoneOffset(),
): { storeOffset: number; deviceOffset: number } | null {
   const storeOffset = getTimeZoneOffsetMinutes(storeTimeZone, now);
   return storeOffset === deviceOffsetMinutes
      ? null
      : { storeOffset, deviceOffset: deviceOffsetMinutes };
}
